/** @jsxImportSource @emotion/react */
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import {
  usClassroomPageSync,
  useClipPageSync,
  useHubPageSync,
  useTestPageSync,
  useClozePageSync,
  useHomePageSync,
  useVocabularyBookPageSync,
  useSpeakingPageSync,
} from '@hook/iyagigo/useSync';
import DialogModal from '@component/iyagigo/modal/dialog-modal';
import NoticeModal from '@component/iyagigo/modal/notice-modal';
import ContentModal from '@component/iyagigo/modal/content-modal';
import NotificationToast from '@component/iyagigo/toast/notification-toast';
import PageToast from '@component/iyagigo/toast/page-toast';
import WallToast from '@component/iyagigo/toast/wall-toast';
import { useApplicationDownloadToast, useNoticeModal } from '@hook/iyagigo/useNotification';
import { useTypedSelector } from '@hook/common/useStore';
import { usePushMessage, useWebviewStatus } from '@hook/common/useApplication';
import { useOpenAttendance, useStudyConfigurationSync } from '@hook/iyagigo/useStudent';
import { useCacheInitPage, useMidnightPage, useStudyingInit } from '@hook/iyagigo/useInit';
import ServicePageGuard from '@component/iyagigo/common/study-page-guard';

import { useDownloadSound } from '@hook/common/useSound';
import { useSyncSubscription } from '@hook/common/usePurchase';

// not found page
import NotFoundPage from '@page/error/not-found';
// landing page
import LandingPage from '@page/iyagigo/landing-page';
// study plan page
import StudyPlanPage from '@page/iyagigo/study-plan-page';
// home page
import HomeBeginPage from '@page/iyagigo/home-page/begin-page';
import WorksheetPage from '@page/iyagigo/worksheet-page';
// cloze page
import ClozeHomePage from '@page/iyagigo/cloze-page/home-page';
import ClozePage from '@page/iyagigo/cloze-page/study-page';
import ClozeNotePage from '@page/iyagigo/cloze-page/note-page';
// vocabulary-book page
import VocabularyBookHomePage from '@page/iyagigo/vocabulary-book-page/home-page';
import VocabularyBookStudyPage from '@page/iyagigo/vocabulary-book-page/study-page';
import VocabularyBookReviewPage from '@page/iyagigo/vocabulary-book-page/review-page';
import VocabularyBookNotePage from '@page/iyagigo/vocabulary-book-page/note-page';
// classroom page (일본어교실 — 글자/기초문법 입문)
import ClassroomHomePage from '@page/iyagigo/classroom-page/home-page';
import CharacterPage from '@page/iyagigo/classroom-page/character-page';
import ClassroomExpressionPage from '@page/iyagigo/classroom-page/classroom-expression-page';
import ClassroomConversationPage from '@page/iyagigo/classroom-page/classroom-conversation-page';
import GrammarPage from '@page/iyagigo/classroom-page/grammar-page';
// clip page (일상표현 — 생생표현/간편이야기)
// import ClipHomePage from '@page/iyagigo/clip-page/home-page';
// import AuthenticExpressionPage from '@page/iyagigo/clip-page/authentic-expression-page';
// import MiniStoryPage from '@page/iyagigo/clip-page/mini-story-page';
// ai page (AI - ai말하기)
import SpeakingHomePage from '@page/iyagigo/speaking-page/home-page';
import SpeakingStudyPage from '@page/iyagigo/speaking-page/study-page';
// test page
import TestHomePage from '@page/iyagigo/test-page/home-page';
import ProblemPage from '@page/iyagigo/test-page/problem-page';
// studio page
import StudioHomePage from '@page/iyagigo/studio-page/home-page';
import StatisticPage from '@page/iyagigo/studio-page/statistic-page';
import ConfigurationPage from '@page/iyagigo/studio-page/configuration-page';
// hub page
import HubHomePage from '@page/iyagigo/hub-page/home-page';
import HelpPage from '@page/iyagigo/hub-page/help-page';
import AccountPage from '@page/iyagigo/hub-page/account-page';
import ContactPage from '@page/iyagigo/hub-page/contact-page';
import ApplicationMetadataPage from '@page/iyagigo/hub-page/application-metadata-page';
import SubscriptionPage from '@page/iyagigo/hub-page/subscription-page';

const IyagigoApp = () => {
  // useCacheInitPage();

  // ###################################
  // 학습중 플래그 초기화
  // - 학습 도중 앱 강제 종료 시 persist된 isStudying: true가 살아남아
  //   재실행 후 학습언어 변경/메뉴 이동에서 workbook-quit 모달이 잘못 뜨는 것을 방지
  // ###################################
  useStudyingInit();

  // ###################################
  // WebView 준비 완료 알림 + Push Token 수신
  // ###################################
  usePushMessage();
  useWebviewStatus();

  // ###################################
  // 구독 상태 동기화 (RN ↔ 백엔드 DB 부정합 보정)
  // ###################################
  useSyncSubscription();

  // ###################################
  // 효과음 mp3 미리 다운로드
  // ###################################
  useDownloadSound();

  // ###################################
  // url path,page <-> navigator sub menu 동기화
  // ###################################
  useTestPageSync();
  useHubPageSync();
  useHomePageSync();
  useClozePageSync();
  usClassroomPageSync();
  useClipPageSync();
  useSpeakingPageSync();
  useVocabularyBookPageSync();

  // ###################################
  // 자정 초기화
  // ###################################
  useMidnightPage();

  // ###################################
  // student 동기화
  // - 학습언어
  // - 시험목표
  // - 출석체크 (OPEN)
  // ###################################
  useOpenAttendance();
  useStudyConfigurationSync();

  // ###################################
  // Application Download Toast
  // ###################################
  useApplicationDownloadToast();

  // ###################################
  // 공지사항 모달 (이벤트, 점검, 출시 공지 등)
  // ###################################
  useNoticeModal();

  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const isDialogModal = useTypedSelector((state) => state.rootReducer?.modalReducer?.isDialogModal) ?? false;
  const isNoticeModal = useTypedSelector((state) => state.rootReducer?.modalReducer?.isNoticeModal) ?? false;
  const isContentModal = useTypedSelector((state) => state.rootReducer?.modalReducer?.isContentModal) ?? false;
  const isNotificationToast =
    useTypedSelector((state) => state.rootReducer?.modalReducer?.isNotificationToast) ?? false;
  const isPageToast = useTypedSelector((state) => state.rootReducer?.modalReducer?.isPageToast) ?? false;
  const isWallToast = useTypedSelector((state) => state.rootReducer?.modalReducer?.isWallToast) ?? false;
  return (
    <>
      {isDialogModal && <DialogModal />}
      {isNoticeModal && <NoticeModal />}
      {isContentModal && <ContentModal />}
      {isNotificationToast && <NotificationToast />}
      {isPageToast && <PageToast />}
      {isWallToast && <WallToast />}
      <Routes>
        {/* begin */}
        {/*<Route path='/' element={<Navigate replace to={IYAGIGO_PATH.BEGIN_PATH} />} />*/}
        <Route path='/' element={<Navigate replace to={IYAGIGO_PATH.LANDING_PATH} />} />
        <Route path={IYAGIGO_PATH.LANDING_PATH} element={<LandingPage />} />
        {/* ############### study plan (첫 학습 전 학습 계획) ############### */}
        <Route path={IYAGIGO_PATH.STUDY_PLAN_PATH} element={<StudyPlanPage />} />
        {/* ############### study plan 완료 후 접근 가능 ############### */}
        <Route element={<ServicePageGuard />}>
          {/* ############### app ############### */}
          <Route path={IYAGIGO_PATH.APP_PATH} element={<Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />} />
          <Route
            path={IYAGIGO_PATH.BRIDGE_PATH}
            element={<Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />}
          />
          {/* ############### home ############### */}
          <Route path={IYAGIGO_PATH.HOME_BEGIN_PATH} element={<HomeBeginPage />} />
          <Route path={IYAGIGO_PATH.WORKSHEET_PATH} element={<WorksheetPage />} />
          {/* ############### classroom (일본어교실 — 글자/기초문법 입문) ############### */}
          <Route path={IYAGIGO_PATH.CLASSROOM_HOME_PATH} element={<ClassroomHomePage />} />
          <Route path={IYAGIGO_PATH.CLASSROOM_CHARACTER_PATH} element={<CharacterPage />} />
          <Route path={IYAGIGO_PATH.CLASSROOM_EXPRESSION_PATH} element={<ClassroomExpressionPage />} />
          <Route path={IYAGIGO_PATH.CLASSROOM_CONVERSATION_PATH} element={<ClassroomConversationPage />} />
          <Route path={IYAGIGO_PATH.CLASSROOM_GRAMMAR_PATH} element={<GrammarPage />} />
          {/* ############### AI conversation ############### */}
          <Route path={IYAGIGO_PATH.SPEAKING_HOME_PATH} element={<SpeakingHomePage />} />
          <Route path={IYAGIGO_PATH.SPEAKING_SESSION_PATH} element={<SpeakingStudyPage />} />
          {/* ############### clip (일상표현 — 생생표현/간편이야기) ############### */}
          {/* 기존 Clip 라우트 보존
          <Route path={IYAGIGO_PATH.CLIP_HOME_PATH} element={<ClipHomePage />} />
          <Route path={IYAGIGO_PATH.CLIP_AUTHENTIC_EXPRESSION_PATH} element={<AuthenticExpressionPage />} />
          <Route path={IYAGIGO_PATH.CLIP_MINI_STORY_PATH} element={<MiniStoryPage />} />
          */}
          {/* ############### cloze ############### */}
          <Route path={IYAGIGO_PATH.CLOZE_HOME_PATH} element={<ClozeHomePage />} />
          <Route path={IYAGIGO_PATH.CLOZE_STUDY_PATH} element={<ClozePage />} />
          <Route
            path={IYAGIGO_PATH.CLOZE_NOTE_PATH}
            element={isAuthenticated ? <ClozeNotePage /> : <Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />}
          />
          {/* ############### vocabulary-book ############### */}
          <Route path={IYAGIGO_PATH.VOCABULARY_BOOK_HOME_PATH} element={<VocabularyBookHomePage />} />
          <Route path={IYAGIGO_PATH.VOCABULARY_BOOK_STUDY_PATH} element={<VocabularyBookStudyPage />} />
          <Route path={IYAGIGO_PATH.VOCABULARY_BOOK_REVIEW_PATH} element={<VocabularyBookReviewPage />} />
          <Route path={IYAGIGO_PATH.VOCABULARY_BOOK_NOTE_PATH} element={<VocabularyBookNotePage />} />
          {/* ############### test ############### */}
          <Route path={IYAGIGO_PATH.TEST_HOME_PATH} element={<TestHomePage />} />
          <Route path={IYAGIGO_PATH.TEST_PROBLEM_PATH} element={<ProblemPage />} />
          {/* ############### studio ############### */}
          <Route
            path={IYAGIGO_PATH.STUDIO_PATH}
            element={isAuthenticated ? <StudioHomePage /> : <Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />}
          />
          <Route
            path={IYAGIGO_PATH.STUDIO_CONFIGURATION_PATH}
            element={
              isAuthenticated ? <ConfigurationPage /> : <Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />
            }
          />
          <Route
            path={IYAGIGO_PATH.STUDIO_STATISTIC_PATH}
            element={isAuthenticated ? <StatisticPage /> : <Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />}
          />
          {/* ############### hub ############### */}
          <Route path={IYAGIGO_PATH.HUB_HOME_PATH} element={<HubHomePage />} />
          <Route path={IYAGIGO_PATH.HUB_HELP_PATH} element={<HelpPage />} />
          <Route path={IYAGIGO_PATH.HUB_CONTACT_PATH} element={<ContactPage />} />
          <Route path={IYAGIGO_PATH.HUB_APPLICATION_METADATA_PATH} element={<ApplicationMetadataPage />} />
          <Route path={IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH} element={<SubscriptionPage />} />
          <Route
            path={IYAGIGO_PATH.HUB_ACCOUNT_PATH}
            element={isAuthenticated ? <AccountPage /> : <Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />}
          />
          {/* 인증확인후, 401 페이지로 보내기*/}
          <Route
            path={IYAGIGO_PATH.STUDIO_PATH}
            element={
              isAuthenticated ? <ConfigurationPage /> : <Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />
            }
          />
          {/* ############### not found page ############### */}
          <Route path='*' element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
};

export default IyagigoApp;
